import {
    LitElement,
    css,
    html,
    customElement,
    property,
    classMap,
    queryAsync,
} from "@/wc/registry/lib/framework/lit/index.js";
const SUPPORTED_TYPE = ["info", "delete", "unsaved-alert"];

@customElement("c-confirmation-modal")
export default class ConfirmationModal extends LitElement {
    @property({ attribute: "data-title" })
    title: string;

    @property({ attribute: "data-type" })
    type: string = "delete";

    @property({ attribute: "data-confirm-button-text" })
    confirmButtonText: string = "Delete";

    @property({ attribute: "data-cancel-button-text" })
    cancelButtonText: string = "Cancel";

    @property({ attribute: "data-message" })
    message: string;

    @queryAsync(".slot") slotElement;

    render() {
        return html`
            <div class="modal">
                <div class="modal-content">
                    <div class="container">
                        <button
                            class="close"
                            @click=${() => this.dispatchCloseModalEvent()}
                        >
                            <span>&times;</span>
                        </button>

                        <slot name="custom-modal" class="slot"
                            >${this.defaultConfirmationTemplate()}
                        </slot>
                    </div>
                </div>
            </div>
        `;
    }

    defaultConfirmationTemplate() {
        const confirmButtonClasses = {
            "info-btn": this.type === "info",
            "delete-btn":
                this.type === "delete" || this.type === "unsaved-alert",
            "confirmation-btn": true,
        };
        return html`
            <h1>${this.title}</h1>
            <p>${this.message}</p>

            <div class="clearfix">
                <button
                    type="button"
                    class="cancel-btn"
                    @click=${() => this.dispatchCloseModalEvent()}
                >
                    ${this.cancelButtonText}
                </button>
                <button
                    type="button"
                    class=${classMap(confirmButtonClasses)}
                    @click=${() => this.dispatchConfirmButtonEvent()}
                >
                    ${this.confirmButtonText}
                </button>
            </div>
        `;
    }

    dispatchConfirmButtonEvent() {
        if (this.type === "unsaved-alert") {
            this.dispatchEvent(
                new CustomEvent("c-confirmation-modal:leave", {
                    bubbles: true,
                    composed: true,
                    detail: this.message,
                })
            );
            this.remove();
            return;
        }

        this.dispatchEvent(
            new CustomEvent("c-confirmation-modal:confirm", {
                bubbles: true,
                composed: true,
                detail: this.message,
            })
        );
        this.remove();
    }

    dispatchCloseModalEvent() {
        this.dispatchEvent(
            new CustomEvent("c-confirmation-modal:close", {
                bubbles: true,
                composed: true,
            })
        );
        this.remove();
    }

    async isConfigured() {
        const slot = await this.slotElement;
        // If the slot is empty, then the user has to provide the configuration manually
        if (slot.assignedNodes().length === 0) {
            if (this.type && !SUPPORTED_TYPE.includes(this.type)) {
                throw Error(
                    `'Invalid data-type provided in ${this.tagName}'. ${SUPPORTED_TYPE} are some of the supported types`
                );
            }
            if (!this.title) {
                throw Error(
                    `'${this.tagName}' requires the 'data-title ' attribute to be configured`
                );
            }

            if (!this.message) {
                throw Error(
                    `'${this.tagName}' requires the 'data-message' attribute to be configured`
                );
            }
        }
    }

    connectedCallback() {
        super.connectedCallback();
        this.isConfigured();
    }

    static styles = css`
        :host {
            --stekz-default-primary-color: #842cff;
        }

        p {
            font-size: 14px;
        }

        * {
            box-sizing: border-box;
        }

        /* Set a style for all buttons */
        button {
            color: white;
            padding: 14px 20px;
            border-radius: 10px;
            margin: 8px 0;
            border: none;
            cursor: pointer;
            opacity: 0.9;
        }

        button:hover {
            opacity: 1;
        }

        /* Float cancel and delete buttons and add an equal width */
        .cancel-btn,
        .confirmation-btn {
            float: left;
            width: 40%;
        }

        /* Add a color to the cancel button */
        .cancel-btn {
            background-color: #ccc;
            color: black;
            font-weight: bold;
        }

        /* Add a color to the delete button */
        .delete-btn {
            background-color: rgb(192, 61, 9);
            font-weight: bold;
        }

        /* Add a color to the info button */
        .info-btn {
            background-color: var(
                --primary-color,
                var(--stekz-default-primary-color)
            );
            font-weight: bold;
        }

        /* Add padding and center-align text to the container */
        .container {
            padding: 16px;
            text-align: center;
        }

        /* The Modal (background) */
        .modal {
            display: flex;
            position: fixed; /* Stay in place */
            z-index: 9999; /* Sit on top */
            left: 0;
            top: 0;
            width: 100%; /* Full width */
            height: 100%; /* Full height */
            overflow: auto; /* Enable scroll if needed */
            background-color: #00000070;
            align-items: flex-start;
        }

        /* Modal Content/Box */
        .modal-content {
            background-color: #fefefe;
            margin: 5% auto 15% auto; /* 5% from the top, 15% from the bottom and centered */
            border: 1px solid #888;
            width: 30%; /* Could be more or less, depending on screen size */
            height: fit-content;
            border-radius: 20px;
        }

        /* Style the horizontal ruler */
        hr {
            border: 1px solid #f1f1f1;
            margin-bottom: 25px;
        }

        /* The Modal Close Button (x) */
        .close {
            margin: 0px;
            padding: 0px;
            color: black;
            background-color: white;
            float: right;
        }

        .close > span {
            font-size: 30px;
        }

        .close:hover,
        .close:focus {
            color: #f44336;
            cursor: pointer;
        }

        .clearfix {
            display: flex;
            justify-content: space-around;
        }

        /* Change styles for cancel button and delete button on extra small screens */
        @media screen and (max-width: 300px) {
            .cancel-btn,
            .confirmation-btn {
                width: 100%;
            }
        }
    `;
}
